import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { rankByRecommendations } from '@atlassian/search-client/rank-by-recommendations';

import { productCategories } from '../../../../constants/product-categories';
import {
	type PrimaryProductKey,
	type ProductKeys,
	ProductKeys as ProductKeysObject,
} from '../../../../constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../../constants/schemas/3p-config';
import { getNestedProducts } from './get-nested-products';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getNestedProducts } from './get-nested-products';

export enum Sections {
	VISIBLE_PRIMARY_PRODUCTS = 'visible-primary-products',
	VISIBLE_PRODUCTS_WITH_RESULTS = 'visible-products-with-results',
	VISIBLE_PRODUCTS_TO_AUTH = 'visible-products-to-auth',
	HIDDEN_PRODUCTS = 'hidden-products',
	NESTED_PRODUCTS = 'nested-products',
}

const PRODUCTS_WITH_RESULTS_DISPLAY_LIMIT = 10;
const PRODUCTS_TO_AUTH_DISPLAY_LIMIT = 3;

export interface getProductOrderWithDynamicSectionsArgs {
	primaryProduct?: PrimaryProductKey;
	availableProducts: ProductKeys[];
	productResultCountMap: { [key: string]: number };
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	isProductHiddenByUser: (productKey: ProductKeys) => boolean;
	ignoreCounts?: boolean;
	recommended3pApps: string[];
}

const sortByResultCount =
	(productResultCountMap: { [key: string]: number }) =>
	(a: ProductKeys, b: ProductKeys): number => {
		const aCount = productResultCountMap[a];
		const bCount = productResultCountMap[b];
		if (aCount === undefined) {
			return 1;
		}
		if (bCount === undefined) {
			return -1;
		}
		return bCount - aCount;
	};

const isPrimaryProduct = (productKey: ProductKeys, primaryProduct?: PrimaryProductKey): boolean => {
	const isAtlas = primaryProduct === ProductKeysObject.Atlas;
	if (isAtlas) {
		return productKey === ProductKeysObject.Confluence || productKey === ProductKeysObject.Jira;
	}
	return productKey === primaryProduct;
};

// Type guard to check if a string is a ProductKeys
function isProductKey(availableProducts: ProductKeys[], key: string): key is ProductKeys {
	return availableProducts.includes(key as ProductKeys);
}

/**
 * Creates an aggregated count map where counts are summed up for individual products and their
 * nested products (e.g. Confluence = Confluence + Confluence DC).
 */
const createAggregatedCountMap = (
	productResultCountMap: { [key: string]: number },
	availableProducts: ProductKeys[],
): { [key: string]: number } => {
	const aggregatedCountMap = { ...productResultCountMap };

	// For each product category, aggregate counts to the primary product
	Object.values(productCategories).forEach((categoryConfig) => {
		const primaryProductKey = categoryConfig.productCategory as ProductKeys;
		let aggregatedCount = 0;

		categoryConfig.products.forEach((productKey) => {
			const count = productResultCountMap[productKey] || 0;
			aggregatedCount += count;
		});

		// Only set aggregated count if the primary product is available
		if (availableProducts.includes(primaryProductKey) && aggregatedCount > 0) {
			aggregatedCountMap[primaryProductKey] = aggregatedCount;
		}
	});

	return aggregatedCountMap;
};

/**
 * Returns a list of product options ordered and grouped into sections.
 *
 * @param primaryProduct - a product where the experience is served (since it affects ordering)
 * @param availableProducts - all products in a default ordered which is used as a tiebreaker
 * @param productResultCountMap - map of product to result count
 * @param thirdPartyConfigs - 3P configs to determine which products need auth
 * @param isProductHiddenByUser - a function to check if a product is hidden by the user (in Local Storage)
 * @param ignoreCounts - if true, ignores result counts and sorts by the default order
 */
export const getProductOrderWithDynamicSections: ({
	primaryProduct,
	availableProducts,
	productResultCountMap,
	thirdPartyConfigs,
	isProductHiddenByUser,
	ignoreCounts,
	recommended3pApps,
}: getProductOrderWithDynamicSectionsArgs) => Map<Sections, string[]> = ({
	primaryProduct,
	availableProducts,
	productResultCountMap,
	thirdPartyConfigs,
	isProductHiddenByUser,
	ignoreCounts = false,
	recommended3pApps,
}: getProductOrderWithDynamicSectionsArgs) => {
	const sections = new Map<Sections, ProductKeys[]>([
		[Sections.VISIBLE_PRIMARY_PRODUCTS, []],
		[Sections.VISIBLE_PRODUCTS_WITH_RESULTS, []],
		[Sections.VISIBLE_PRODUCTS_TO_AUTH, []],
		[Sections.HIDDEN_PRODUCTS, []],
		[Sections.NESTED_PRODUCTS, []],
	]);

	const nestedProducts = getNestedProducts();

	// Create aggregated count map which reflects both the individual product and nested DC products
	const aggregatedCountMap = createAggregatedCountMap(productResultCountMap, availableProducts);

	// Separate variable since we need to slice these later up to the limit
	const productsWithResults: ProductKeys[] = [];
	const productsToAuth: ProductKeys[] = [];

	// Iterate over all products and do an initial categorisation into sections.
	// For VISIBLE_PRODUCTS_WITH_RESULTS and VISIBLE_PRODUCTS_TO_AUTH we will do a second pass to enforce limits.
	for (const productKey of availableProducts) {
		if (isPrimaryProduct(productKey, primaryProduct)) {
			sections.get(Sections.VISIBLE_PRIMARY_PRODUCTS)!.push(productKey);
		} else if (isProductHiddenByUser(productKey)) {
			// Check hidden products after primary product check so that primary product is never hidden.
			sections.get(Sections.HIDDEN_PRODUCTS)!.push(productKey);
		} else if (nestedProducts.has(productKey)) {
			sections.get(Sections.NESTED_PRODUCTS)!.push(productKey);
		} else if (thirdPartyConfigs[productKey as ProductKeys3P]?.userNeedsOAuth) {
			productsToAuth.push(productKey);
		} else {
			// Check if this is a fanout product - fanout products should be visible even without results
			// because they represent multiple datasources that might have results
			const isFanoutProduct =
				thirdPartyConfigs[productKey as ProductKeys3P]?.fanOutConnectorSources &&
				(productKey !== ProductKeysObject.AtlassianWebCrawler ||
					fg('webcrawler-fanout-ui-sidebar-enabled'));

			if (ignoreCounts || (aggregatedCountMap[productKey] ?? 0) > 0 || isFanoutProduct) {
				productsWithResults.push(productKey);
			} else {
				sections.get(Sections.HIDDEN_PRODUCTS)!.push(productKey);
			}
		}
	}

	// A util to sort by counts (using aggregated counts)
	const sortByCountIfNeeded = ignoreCounts ? () => 0 : sortByResultCount(aggregatedCountMap);

	// Products with results- sort, extract the TOP N and push the overflow to HIDDEN_PRODUCTS
	productsWithResults.sort(sortByCountIfNeeded);
	// TODO: we should include NESTED_PRODUCTS count when calculating how many items to show here

	sections.set(
		Sections.VISIBLE_PRODUCTS_WITH_RESULTS,
		productsWithResults.slice(0, PRODUCTS_WITH_RESULTS_DISPLAY_LIMIT),
	);
	sections
		.get(Sections.HIDDEN_PRODUCTS)!
		.push(...productsWithResults.slice(PRODUCTS_WITH_RESULTS_DISPLAY_LIMIT));

	const rankedAuth = rankByRecommendations({
		items: productsToAuth,
		recommendedIds: recommended3pApps.filter((app) => isProductKey(availableProducts, app)),
		idOf: (app) => app,
	});
	sections.set(
		Sections.VISIBLE_PRODUCTS_TO_AUTH,
		rankedAuth.slice(0, PRODUCTS_TO_AUTH_DISPLAY_LIMIT),
	);
	sections.get(Sections.HIDDEN_PRODUCTS)!.push(...rankedAuth.slice(PRODUCTS_TO_AUTH_DISPLAY_LIMIT));

	// Finally sort HIDDEN_PRODUCTS section by count
	sections.get(Sections.HIDDEN_PRODUCTS)!.sort(sortByCountIfNeeded);

	return sections;
};

/** Keeps the saved order while updating which products are available in the selected sites. */
export const reconcilePersonalisedProductSections = ({
	personalisedProductSections,
	...args
}: Omit<getProductOrderWithDynamicSectionsArgs, 'ignoreCounts' | 'productResultCountMap'> & {
	personalisedProductSections: Map<Sections, ProductKeys[]>;
}): Map<Sections, ProductKeys[]> => {
	const availableProducts = new Set(args.availableProducts);
	const savedProducts = new Set(Array.from(personalisedProductSections.values()).flat());
	const addedProducts = args.availableProducts.filter((product) => !savedProducts.has(product));

	if (addedProducts.length === 0 && savedProducts.size === availableProducts.size) {
		return personalisedProductSections;
	}

	// Counts can still belong to the previous site selection. New products must remain
	// available while those counts load, so use their default sections instead.
	const sections = getProductOrderWithDynamicSections({
		...args,
		availableProducts: addedProducts,
		productResultCountMap: {},
		ignoreCounts: true,
	});

	for (const [section, additions] of sections) {
		sections.set(section, [
			...(personalisedProductSections.get(section) ?? []).filter((product) =>
				availableProducts.has(product),
			),
			...additions,
		]);
	}

	// Keep the existing section limits; overflow remains available in Show more.
	for (const [section, limit] of [
		[Sections.VISIBLE_PRODUCTS_WITH_RESULTS, PRODUCTS_WITH_RESULTS_DISPLAY_LIMIT],
		[Sections.VISIBLE_PRODUCTS_TO_AUTH, PRODUCTS_TO_AUTH_DISPLAY_LIMIT],
	] as const) {
		const products = sections.get(section)!;
		sections.get(Sections.HIDDEN_PRODUCTS)!.push(...products.splice(limit));
	}

	return sections;
};
